import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useCardsStore, usePrefsStore, useSavedPlansStore, pruneAbandonedCards } from "@music-app/persistence";
import { AppLayout } from "./layouts/AppLayout.js";
import { ReviewSessionPage } from "./routes/ReviewSession.js";
import { PlacementPage } from "./routes/Placement.js";
import { LibraryPage } from "./routes/Library.js";
import { ProgressPage } from "./routes/Progress.js";
import { SettingsPage } from "./routes/Settings.js";
import { AboutPage } from "./routes/About.js";
import { PrivacyPage } from "./routes/Privacy.js";
import { TermsPage } from "./routes/Terms.js";
import { LearnPage } from "./routes/Learn.js";
import { ExerciseHubPage } from "./routes/ExerciseHub.js";
import { ReviewHubPage } from "./routes/ReviewHub.js";
import { ReviewCustomizePage } from "./routes/ReviewCustomize.js";
import { ScaleExercisePage } from "./routes/ScaleExercise.js";
import { IntervalExercisePage } from "./routes/IntervalExercise.js";
import { ChordQualityExercisePage } from "./routes/ChordQualityExercise.js";
import { CadenceExercisePage } from "./routes/CadenceExercise.js";
import { ChordInversionExercisePage } from "./routes/ChordInversionExercise.js";
import { ModeExercisePage } from "./routes/ModeExercise.js";
import { MelodicDictationExercisePage } from "./routes/MelodicDictationExercise.js";
import { RhythmicDictationExercisePage } from "./routes/RhythmicDictationExercise.js";
import { ProgressionExercisePage } from "./routes/ProgressionExercise.js";
import { ExerciseMixPage } from "./routes/ExerciseMix.js";
import { TopicDrillPage } from "./routes/TopicDrill.js";
import { GuitarHubPage } from "./routes/GuitarHub.js";
import { FretIntervalsExercisePage } from "./routes/FretIntervalsExercise.js";
import { FretTriadsExercisePage } from "./routes/FretTriadsExercise.js";
import { FretInversionsExercisePage } from "./routes/FretInversionsExercise.js";
import { FretCagedExercisePage } from "./routes/FretCagedExercise.js";
import { FretCagedAnchoringExercisePage } from "./routes/FretCagedAnchoringExercise.js";
import { FretDiatonicExercisePage } from "./routes/FretDiatonicExercise.js";
import { FretScaleDegreeExercisePage } from "./routes/FretScaleDegreeExercise.js";
import { FretScaleDegreesCagedExercisePage } from "./routes/FretScaleDegreesCagedExercise.js";
import { FretDiatonicMinorExercisePage } from "./routes/FretDiatonicMinorExercise.js";
import { FretSeventhExercisePage } from "./routes/FretSeventhExercise.js";
import { consumeUnlockTrigger } from "./lib/unlock-trigger.js";
import { useSyncWiring } from "./lib/sync-wiring.js";
import { DemoPersonaSwitch } from "./components/DemoPersonaSwitch.js";
import { FretboardDevPage } from "./routes/FretboardDev.js";
import { ReviewExplainPage } from "./routes/ReviewExplain.js";
import { ReviewWhyPage } from "./routes/ReviewWhy.js";
import { AuthCallbackPage } from "./routes/AuthCallback.js";
import { PlanBuilderPage } from "./routes/PlanBuilder.js";

/**
 * Lesson view is lazy because the lesson primitives (PlayButton +
 * MiniExercise + Score + audio context + MDXProvider) and the
 * dynamically-imported MDX content don't need to ship in the entry
 * chunk. Per spec §14, per-lesson chunks should land < 30 KB gz; this
 * route is the seam that makes that splitting actually happen.
 */
const LearnLessonPage = lazy(() =>
  import("./routes/LearnLesson.js").then((m) => ({ default: m.LearnLessonPage })),
);

function LessonSuspenseFallback(): ReactNode {
  return (
    <div className="max-w-2xl mx-auto pt-8">
      <p className="text-inkSoft italic">Loading lesson…</p>
    </div>
  );
}

export default function App() {
  const hydrateCards = useCardsStore((s) => s.hydrate);
  const hydratePrefs = usePrefsStore((s) => s.hydrate);
  const hydratePlans = useSavedPlansStore((s) => s.hydrate);
  const prefsLoaded = usePrefsStore((s) => s.loaded);
  const setUnlockEverything = usePrefsStore((s) => s.setUnlockEverything);

  useSyncWiring();

  useEffect(() => {
    // One-time local cleanup of abandoned orphan cards before the store loads,
    // so the forecast/stats never count them (covers offline / not-signed-in).
    void pruneAbandonedCards().finally(() => void hydrateCards());
    void hydratePrefs();
    void hydratePlans();
  }, [hydrateCards, hydratePrefs, hydratePlans]);

  // Hidden URL gate — DEV ONLY. In dev, a `#unlock-<secret>` (or
  // `?unlock=<secret>`) URL matching the build-time `VITE_UNLOCK_SECRET`
  // flips the unlock pref. Gated on `import.meta.env.DEV` so the branch (and
  // the secret reference) is dead-code-eliminated from production bundles —
  // prod unlock comes solely from the tester/qa roles. Without a secret
  // configured, the gate is inert even in dev.
  useEffect(() => {
    if (!prefsLoaded) return;
    if (import.meta.env.DEV && consumeUnlockTrigger(import.meta.env.VITE_UNLOCK_SECRET)) {
      void setUnlockEverything(true);
    }
  }, [prefsLoaded, setUnlockEverything]);

  return (
    <>
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Navigate to="/review" replace />} />
        {/* ── review (FSRS daily session) ── */}
        <Route path="/review" element={<ReviewHubPage />} />
        <Route path="/review/customize" element={<ReviewCustomizePage />} />
        <Route path="/review/plans/new" element={<PlanBuilderPage />} />
        <Route path="/review/plans/:id/edit" element={<PlanBuilderPage />} />
        <Route path="/review/session" element={<ReviewSessionPage />} />
        <Route path="/review/why" element={<ReviewWhyPage />} />
        {/* redirects for old /practice/* URLs */}
        <Route path="/practice" element={<Navigate to="/review" replace />} />
        <Route path="/practice/customize" element={<Navigate to="/review/customize" replace />} />
        <Route path="/review/plans/new" element={<Navigate to="/review/plans/new" replace />} />
        <Route path="/practice/session" element={<Navigate to="/review/session" replace />} />
        <Route path="/practice/why" element={<Navigate to="/review/why" replace />} />
        {/* ── exercise hub ── */}
        <Route path="/exercise" element={<ExerciseHubPage />} />
        <Route path="/exercise/scales" element={<ScaleExercisePage />} />
        <Route path="/exercise/intervals" element={<IntervalExercisePage />} />
        <Route path="/exercise/chords" element={<ChordQualityExercisePage />} />
        <Route path="/exercise/cadences" element={<CadenceExercisePage />} />
        <Route path="/exercise/inversions" element={<ChordInversionExercisePage />} />
        <Route path="/exercise/modes" element={<ModeExercisePage />} />
        <Route path="/exercise/dictation" element={<MelodicDictationExercisePage />} />
        <Route path="/exercise/rhythmic-dictation" element={<RhythmicDictationExercisePage />} />
        <Route path="/exercise/progressions" element={<ProgressionExercisePage />} />
        <Route path="/exercise/mix/:mixId" element={<ExerciseMixPage />} />
        <Route path="/exercise/topic/:nodeId" element={<TopicDrillPage />} />
        {/* redirects for old /drill/* URLs */}
        <Route path="/exercise" element={<Navigate to="/exercise" replace />} />
        <Route path="/exercise/scales" element={<Navigate to="/exercise/scales" replace />} />
        <Route path="/exercise/intervals" element={<Navigate to="/exercise/intervals" replace />} />
        <Route path="/exercise/chords" element={<Navigate to="/exercise/chords" replace />} />
        <Route path="/exercise/cadences" element={<Navigate to="/exercise/cadences" replace />} />
        <Route path="/exercise/inversions" element={<Navigate to="/exercise/inversions" replace />} />
        <Route path="/exercise/modes" element={<Navigate to="/exercise/modes" replace />} />
        <Route path="/exercise/dictation" element={<Navigate to="/exercise/dictation" replace />} />
        <Route path="/exercise/rhythmic-dictation" element={<Navigate to="/exercise/rhythmic-dictation" replace />} />
        <Route path="/exercise/progressions" element={<Navigate to="/exercise/progressions" replace />} />
        <Route path="/exercise/mix/:mixId" element={<Navigate to="/exercise/mix/:mixId" replace />} />
        <Route path="/drill/fretboard" element={<Navigate to="/guitar" replace />} />
        <Route path="/drill/guitar" element={<Navigate to="/guitar" replace />} />
        {/* ── guitar ── */}
        <Route path="/guitar" element={<GuitarHubPage />} />
        <Route path="/guitar/fret-intervals" element={<FretIntervalsExercisePage />} />
        <Route path="/guitar/fret-scale-degrees" element={<FretScaleDegreeExercisePage />} />
        <Route path="/guitar/fret-triads" element={<FretTriadsExercisePage />} />
        <Route path="/guitar/fret-inversions" element={<FretInversionsExercisePage />} />
        <Route path="/guitar/fret-caged" element={<FretCagedExercisePage />} />
        <Route path="/guitar/fret-caged-anchoring" element={<FretCagedAnchoringExercisePage />} />
        <Route path="/guitar/fret-scale-degrees-caged" element={<FretScaleDegreesCagedExercisePage />} />
        <Route path="/guitar/fret-diatonic" element={<FretDiatonicExercisePage />} />
        <Route path="/guitar/fret-diatonic-minor" element={<FretDiatonicMinorExercisePage />} />
        <Route path="/guitar/fret-seventh" element={<FretSeventhExercisePage />} />
        <Route path="/guitar/placement" element={<PlacementPage variant="guitar" />} />
        {/* ── progress ── */}
        <Route path="/progress" element={<ProgressPage />} />
        <Route path="/stats" element={<Navigate to="/progress" replace />} />
        {/* ── other ── */}
        <Route path="/placement" element={<PlacementPage />} />
        <Route path="/learn" element={<LearnPage />} />
        <Route
          path="/learn/:nodeId"
          element={
            <Suspense fallback={<LessonSuspenseFallback />}>
              <LearnLessonPage />
            </Suspense>
          }
        />
        <Route path="/skill-tree" element={<Navigate to="/progress" replace />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {import.meta.env.DEV && (
          <>
            <Route path="/dev/fretboard" element={<FretboardDevPage />} />
            <Route path="/review/explain" element={<ReviewExplainPage />} />
          </>
        )}
      </Route>
    </Routes>
    <DemoPersonaSwitch />
    </>
  );
}
